import { FC, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { CollapsableCard } from '@/_shared/components/CollapsableCard';
import { StyledTableDrawer, usePaginatedTable } from '@/_shared/components/Table';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentTeamDetailsCall, getDepartmentTeamMembersCall } from '@/departments/state';
import { PAGE_SIZE_TEAM_MEMBERS } from '@/departments/state/constants';
import { LeadManager } from '../LeadManager';
import { MembersList } from '../MembersList';

export const TabDepartmentTeam: FC = () => {
  const t = useTranslations('department.details');

  const { genericResponsiveValue } = useResponsiveStyle();

  const tShared = useTranslations();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const {
    loading: loadingGetDepartmentTeamMembers,
    error: errorGetDepartmentTeamMembers,
    data,
    totalItems,
  } = getDepartmentTeamMembersCall.hook();
  const {
    loading: loadingGetDepartmentTeamDetails,
    error: errorGetDepartmentTeamDetails,
    departmentTeamDetails,
  } = getDepartmentTeamDetailsCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_TEAM_MEMBERS,
    totalItems: totalItems || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const leadManager = useMemo(
    () => departmentTeamDetails?.manager ?? undefined,
    [departmentTeamDetails],
  );

  if (loadingGetDepartmentTeamDetails || loadingGetDepartmentTeamMembers) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetDepartmentTeamMembers || errorGetDepartmentTeamDetails) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  return (
    <Box>
      <Card mt='m' mb='s' display='flex' flexDirection='column'>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: {
              desktop: 'row',
            },
            defaultValue: 'column',
          })}
          alignItems={genericResponsiveValue({
            breakPoints: {
              desktop: 'center',
            },
            defaultValue: 'flex-start',
          })}
        >
          <Typography variant='h1'>
            {departmentTeamDetails?.name !== 'unknown'
              ? `${t('team')} ${departmentTeamDetails?.name}`
              : t('noTeamName')}
          </Typography>
          <Box
            ml={genericResponsiveValue({
              breakPoints: {
                desktop: 'xs',
              },
              defaultValue: 'none',
            })}
            mt={genericResponsiveValue({
              breakPoints: {
                desktop: 'none',
              },
              defaultValue: 'xs',
            })}
            alignItems='center'
            flexDirection='row'
            display='flex'
            flex={1}
          >
            <Svg name='users' size='s' />
            <Typography ml='xxs' variant='h1'>
              {departmentTeamDetails?.stats?.totalEmployees || 0}
            </Typography>
          </Box>
        </Box>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          alignItems='flex-start'
          width={genericResponsiveValue({
            breakPoints: { desktop: '50%' },
            defaultValue: '100%',
          })}
          mb='s'
          backgroundColor={genericResponsiveValue({
            breakPoints: { desktop: 'transparent' },
            defaultValue: 'grey/dark',
          })}
          p={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 's',
          })}
          mt={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
          borderRadius={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'm',
          })}
        >
          <Box flex={1} flexDirection='column' display='flex'>
            <Typography
              mt={genericResponsiveValue({
                breakPoints: { desktop: 'xs' },
                defaultValue: 'none',
              })}
              variant='p2/light'
              color='grey/light'
            >
              {t('validProfiles')}
            </Typography>
            <Typography variant='h1'>{departmentTeamDetails?.stats?.completedEmployees}</Typography>
          </Box>
          <Box flex={1} flexDirection='column' display='flex'>
            <Typography mt='xs' variant='p2/light' color='grey/light'>
              {t('profilesToCorrect')}
            </Typography>
            <Typography
              variant='h1'
              color={departmentTeamDetails?.stats?.invalidEmployees ? 'error' : 'success'}
            >
              {departmentTeamDetails?.stats?.invalidEmployees || 0}
            </Typography>
          </Box>
        </Box>
        <Box display='flex' flexDirection='column'>
          <Typography mt='xs' variant='p2/label'>
            {t('teamManager')}
          </Typography>
          <LeadManager
            loading={loadingGetDepartmentTeamDetails}
            error={errorGetDepartmentTeamDetails}
            paginedProps={{
              ...paginedProps,
              paginatedData: leadManager ? [leadManager] : [],
            }}
            drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
            hidePagination
          />
        </Box>
      </Card>
      <CollapsableCard
        title={departmentTeamDetails?.name !== 'unknown' ? t('teamMembers') : t('members')}
        isCollapsedOnDefault={false}
      >
        <Box
          width='100%'
          minWidth={0}
          minHeight={0}
          overflow={genericResponsiveValue({
            breakPoints: { desktop: 'visible' },
            defaultValue: 'auto',
          })}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <Box
            alignItems='center'
            mb='s'
            display='flex'
            flexDirection='row'
            justifyContent='flex-end'
            width='100%'
          >
            <Box
              display='flex'
              width={genericResponsiveValue({
                breakPoints: { desktop: 'auto' },
                defaultValue: '100%',
              })}
              flexDirection='row'
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
              mt={genericResponsiveValue({
                breakPoints: { desktop: 'xs' },
                defaultValue: 's',
              })}
            >
              <Box
                ml={genericResponsiveValue({
                  breakPoints: { desktop: 'xl' },
                  defaultValue: 'none',
                })}
                alignItems='center'
                flexDirection='row'
                display='flex'
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xxs',
                })}
              >
                <Svg name='users' size='s' />
                <Typography ml='xxs' variant='h1'>
                  {totalItems || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
          <MembersList
            loading={loadingGetDepartmentTeamMembers}
            error={errorGetDepartmentTeamMembers}
            paginedProps={paginedProps}
            drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
          />
        </Box>
      </CollapsableCard>
    </Box>
  );
};
